import { Copy, type LucideIcon } from 'lucide-react';
import type { MouseEvent, ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Shared copy-button styling. To add a new look:
 * 1. Add an `iconState` if the icon needs different idle/copied behavior.
 * 2. Add a `variant` that references a button/icon size and an `iconState`.
 * 3. Use `icon` / `copiedIcon` props for one-off overrides without a new variant.
 */
const COPYABLE_TEXT = {
  iconStates: {
    'hidden-until-hover': {
      idle: 'opacity-0 group-hover:opacity-40',
      copied: 'text-green-600',
    },
    subtle: {
      idle: 'opacity-40 group-hover:opacity-80',
      copied: 'text-green-600 opacity-100',
    },
  },
  variants: {
    default: {
      buttonClass:
        'group flex items-center gap-1.5 font-mono text-xs text-foreground hover:text-primary transition-colors',
      iconClass: 'h-3 w-3 transition-all',
      iconState: 'hidden-until-hover',
      title: 'Copy ID',
    },
    muted: {
      buttonClass:
        'group inline-flex items-center gap-1.5 rounded px-1 font-mono text-[10px] font-normal text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground',
      iconClass: 'h-2.5 w-2.5 transition-all',
      iconState: 'hidden-until-hover',
      title: 'Copy',
    },
  },
} as const;

export type CopyableTextVariant = keyof typeof COPYABLE_TEXT.variants;
export type CopyableTextIconState = keyof typeof COPYABLE_TEXT.iconStates;

export type CopyableTextProps = {
  /** Visible content in the control */
  label: ReactNode;
  /** String passed through to `onCopy` (typically what lands on the clipboard) */
  copyValue: string;
  isCopied: boolean;
  onCopy: (copyValue: string, event: MouseEvent<HTMLButtonElement>) => void;
  variant?: CopyableTextVariant;
  /** Override the variant's icon idle/copied behavior */
  iconState?: CopyableTextIconState;
  title?: string;
  /** Icon shown in the idle state (defaults to Copy) */
  icon?: LucideIcon;
  /** Icon shown after a successful copy (defaults to the idle icon) */
  copiedIcon?: LucideIcon;
  className?: string;
  buttonClassName?: string;
  iconClassName?: string;
};

export function CopyableText({
  label,
  copyValue,
  isCopied,
  onCopy,
  variant = 'default',
  iconState,
  title,
  icon: IconComponent = Copy,
  copiedIcon,
  className,
  buttonClassName,
  iconClassName,
}: CopyableTextProps) {
  const styles = COPYABLE_TEXT.variants[variant];
  const resolvedTitle = title ?? styles.title;
  const resolvedIconState = iconState ?? styles.iconState;
  const iconStyles = COPYABLE_TEXT.iconStates[resolvedIconState];
  const DisplayIcon = isCopied && copiedIcon ? copiedIcon : IconComponent;

  return (
    <button
      type="button"
      onClick={(event) => onCopy(copyValue, event)}
      className={cn(styles.buttonClass, buttonClassName, className)}
      title={resolvedTitle}
    >
      <span className="sr-only">{resolvedTitle} </span>
      {label}
      <DisplayIcon
        className={cn(
          styles.iconClass,
          iconClassName,
          isCopied ? iconStyles.copied : iconStyles.idle
        )}
      />
    </button>
  );
}
